import { t } from "@lingui/macro";
import dayjs from "dayjs";
import { RowType } from "../components/Editable/FormItem/Rows/RowType";
import { Type } from "../redux/Transaction/helper";
import { my } from "make-plural";

export const EXCLUDED_CLASSIFICATION_CODES = ['006'];

export const MyInvoisTransactionHelper = {
    hasItemsWithoutClassificationCode: items => items.some(item => {
        if (item.type === "subtotal" || item.type === 'subtitle' || item.type === RowType.bundle_discount)
            return false; // Skip these rows

        if (item.children?.length > 0) {
            // Check nested items (i.e., bundle)
            return MyInvoisTransactionHelper.hasItemsWithoutClassificationCode(item.children);
        }

        return !item.classification_code;
    }),

    // Returns true if all classifiable items have an excluded classification code
    allItemsAreExcludedClassification: items => {
        const classifiableItems = items.filter(item =>
            item.type !== "subtotal" && item.type !== 'subtitle' && item.type !== RowType.bundle_discount,
        );
        if (classifiableItems.length === 0)
            return false;

        return classifiableItems.every(item => {
            if (item.children?.length > 0)
                return MyInvoisTransactionHelper.allItemsAreExcludedClassification(item.children);

            return EXCLUDED_CLASSIFICATION_CODES.includes(item.classification_code);
        });
    },

    hasExcludedClassificationOnlyTransactions: (batchItems) =>
        batchItems.some(item =>
            MyInvoisTransactionHelper.allItemsAreExcludedClassification([item, ...(item.children ?? [])]),
        ),

    // Check if transaction is huge transaction (> RM10,000)
    isHugeTransaction: (exchangeRate, formItems) => {
        const thresholdAmount = 10000;
        let totalAmount = 0;
        formItems.forEach(item => {
            if (!(item.type === "bundle" || item.type === undefined))
                return;
            const itemTotal = Math.round(item.amount * (exchangeRate || 1) * 100) / 100;
            totalAmount += itemTotal;
        });

        return totalAmount >= thresholdAmount;
    },

    // Check if transaction is huge transaction (> RM10,000)
    isHugeTransactionForBatch: (items, myInvoisStartDate) => {
        if (!myInvoisStartDate)
            return false;

        const thresholdAmount = 10000;
        return items.filter(item => !dayjs(item.date).isBefore(dayjs(myInvoisStartDate), 'day'),
        ).some(item => {
            const exchangeRate = item.exchangeRate || 1;
            const childrenRow = item.children || [];

            let totalAmount = Math.round(item.amount * exchangeRate * 100) / 100;

            childrenRow.forEach(child => {
                const childTotal = Math.round(child.amount * exchangeRate * 100) / 100;
                totalAmount += childTotal;
            });

            return totalAmount >= thresholdAmount; // stops iteration once true
        });
    },

    getShouldVoidTransactionDetails: (transaction, transactionHasNoLinkedItems) => {
        //incoming sync transaction can be void and delete
        if (transaction.is_synced) {
            return {
                transactionAllowVoid: true,
                transactionDisableVoidMsg: null,
            };
        }

        if (transaction.is_consolidated) {
            return {
                transactionAllowVoid: false,
                transactionDisableVoidMsg: t`The transaction is consolidated and cannot be voided.`,
            };
        }

        let disabledDueToLinkedItemsMsg = null;
        switch (transaction.type) {
            case Type.sale_invoice:
            case Type.purchase_bill:
                disabledDueToLinkedItemsMsg = t`The transaction has payment / credit applied and cannot be voided.`;
                break;
            case Type.sale_credit_note:
            case Type.purchase_credit_note:
                disabledDueToLinkedItemsMsg = t`The transaction has credits applied to other transactions and cannot be voided.`;
                break;
            default:
                break;
        }

        // 1. (myinvois_action = NORMAL/EXTERNAL) OR (myinvois_action = VALIDATE + myinvois_document_status = ERROR/INVALID) OR (no myinvois_action for old transacitons):
        //      a) if no payment/credit applied, can void; else cannot.
        // 2. myinvois_action = VALIDATE:
        //      a) myinvois_document_status = QUEUED/SUBMITTED, cannot void.
        //      b) myinvois_document_status = VALID, can void if (no payment/credit applied + current time has to be within 72 hours from validation time); else cannot void.
        //      c) myinvois_document_status = CANCELLED, cannot void.
        let transactionAllowVoid = false,
            transactionDisableVoidMsg = null;
        if (!transaction.myinvois_action ||
            ['NORMAL', 'EXTERNAL'].includes(transaction.myinvois_action) ||
            (transaction.myinvois_action === 'VALIDATE' && ['ERROR', 'INVALID'].includes(transaction.myinvois_document_status))) {
            // if no linked items, can void
            if (transactionHasNoLinkedItems)
                transactionAllowVoid = true;
            else
                transactionDisableVoidMsg = disabledDueToLinkedItemsMsg;
        }

        else if (transaction.myinvois_action === 'VALIDATE') {
            if (['QUEUED', 'SUBMITTED'].includes(transaction.myinvois_document_status)) {
                transactionDisableVoidMsg = t`The transaction is pending submission on Mylnvois and cannot be voided.`;
            }

            else if (transaction.myinvois_document_status === 'VALID') {
                if (
                    // time of void has to be >= validated_at && <= validated_at + 72 hours
                    dayjs().isSameOrAfter(dayjs(transaction.validated_at)) &&
                    dayjs().isSameOrBefore(dayjs(transaction.validated_at).add(72, 'hours'))
                ) {
                    // if no linked items, can void
                    if (transactionHasNoLinkedItems)
                        transactionAllowVoid = true;
                    else
                        transactionDisableVoidMsg = disabledDueToLinkedItemsMsg;
                }
                else {
                    transactionDisableVoidMsg = t`The transaction can only be voided within 72 hours from validation date.`;
                }
            }

            else if (transaction.myinvois_document_status === 'CANCELLED') {
                transactionDisableVoidMsg = t`The transaction has been cancelled and cannot be voided.`;
            }
        }

        return {
            transactionAllowVoid, transactionDisableVoidMsg,
        };
    },

    getAllowUnvoidTransactionDetails: transaction => {
        // 1. (myinvois_action = NORMAL/EXTERNAL) OR (myinvois_action = VALIDATE + myinvois_document_status = ERROR/INVALID) OR (no myinvois_action for old transactions):
        //     allow void.
        // 2. myinvois_action = VALIDATE + myinvois_document_status = CANCELLED:
        //     cannot void.
        let transactionAllowUnvoid = false,
            transactionDisableUnvoidMsg = null;

        if (!transaction.myinvois_action ||
            ['NORMAL', 'EXTERNAL'].includes(transaction.myinvois_action) ||
            (transaction.myinvois_action === 'VALIDATE' && ['ERROR', 'INVALID'].includes(transaction.myinvois_document_status))
        )
            transactionAllowUnvoid = true;

        else if (transaction.myinvois_action === 'VALIDATE' && transaction.myinvois_document_status === 'CANCELLED')
            transactionDisableUnvoidMsg = t`The transaction is cancelled on MyInvois and cannot be unvoided.`;

        return {
            transactionAllowUnvoid, transactionDisableUnvoidMsg,
        };
    },

    getAllowValidateTransaction: date => {
        // future date, block
        if (dayjs().isBefore(dayjs(date)))
            return false;

        // if date is older than 3 days from today,
        // or rather if today - txn date is NOT within 3 days (irregardless of timing), block
        if (dayjs().endOf('day').diff(dayjs(date).startOf('day'), 'days') > 3)
            return false;

        return true;
    },
};
